import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import moment from 'moment';
import Button from "../../../../components/buttons/buttons";
import Table from "../../../../components/tables/tableCols1";
import TableSkeleton from "../../../../components/skeleton/table";
import TableFilter from "../../../../components/tables/tableFilter";
import { PaginationNoRoute } from "../../../../components/pagination/paginationNoRoute";
import EmptyState from "../../../../assets/images/icons/BusinessEmpty.svg";
import { writeFileWithXLSX } from "../../../../components/exports/xlsx";
import { Beat } from "../../../../plugins/spinners.plugin";
import {
  filterShippers,
  filterShippersNoPage,
  searchShippers,
} from "../../../../redux/shippers/shippersActions";

export default function RevenueShippers() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentDate = new Date();

  const { allShippers, filtering, searching } = useSelector(
    (state) => state.shipper,
  );

  const [page, setPage] = useState(1);
  const [searchResults, setSearchResults] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [input, setInput] = useState({
    search: "",
  });
  const isSearchOpen = searchResults !== null;

  const [payLoad] = useState({
    // management_approved: 0,
    // API expects Y-m-d H:i
    date: moment(currentDate).format("YYYY-MM-DD HH:mm"),
  });

  useEffect(() => {
    if (page) {
      dispatch(filterShippers(payLoad, page));
    }
    // eslint-disable-next-line
  }, [payLoad, page]);

  function createTableData(data) {
    const dataToMap = data;
    if (Array.isArray(dataToMap)) {
      const outputArray = [];
      dataToMap.forEach((shipper, index) => {
        const single = {
          num: <span className="text-base font-semibold">{index + 1}</span>,
          name: shipper?.name,
          location: shipper?.city,
          status:
            shipper?.status === "active" ? (
              <span className="text-green-400 font-bold">Active</span>
            ) : (
              <span className="text-gray-400 font-bold">Inactive</span>
            ),
          shipper: shipper,
        };

        outputArray.push({ single });
      });

      return outputArray;
    }
    return [];
  }

  const data = createTableData(searchResults ?? allShippers?.data);

  function createTableNoPage(data) {
    const dataToMap = data;
    if (Array.isArray(dataToMap)) {
      const outputArray = [];
      dataToMap.forEach((shipper) => {
        const single = {
          name: shipper?.shipper_name || shipper?.name,
          location: shipper?.city,
          // status: shipper?.status === "active" ? "Active" : "Inactive",
        };

        outputArray.push(single);
      });

      return outputArray;
    }
    return [];
  }

  const goToSinglePage = (params) => {
    const id = params?.single?.shipper?.id;
    navigate(`/admin/revenue/shipper/${id}/payouts`);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInput({ ...input, [name]: value });
    // Clearing the search box goes back to the paginated list
    if (name === "search" && value.length === 0) {
      setSearchResults(null);
    }
  };

  const handleKeyDown = async (e) => {
    if (e.key === "Enter" && input?.search) {
      const response = await dispatch(searchShippers({ search: input.search }));
      // search may come back paginated or as a plain list
      const results = response?.data?.data ?? response?.data;
      setSearchResults(Array.isArray(results) ? results : []);
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: "",
        hideHeader: false,
        id: "checkbox-table-column",
        columns: [
          {
            Header: "S/N",
            accessor: "single.num",
          },
          {
            Header: "Shipper Name",
            accessor: "single.name",
          },
          {
            Header: "Location",
            accessor: "single.location",
          },
          // {
          //   Header: "Status",
          //   accessor: "single.status",
          // },
        ],
      },
    ],
    [],
  );

  // Exports the search results if searching, otherwise every shipper (not just this page)
  const handleExport = async () => {
    setExporting(true);
    let shippers = searchResults;
    if (!isSearchOpen) {
      const response = await dispatch(filterShippersNoPage(payLoad));
      shippers = response?.data?.data ?? response?.data;
    }
    setExporting(false);
    if (Array.isArray(shippers)) {
      writeFileWithXLSX(createTableNoPage(shippers), {
        filename: "Shippers_Revenue.xlsx",
        download: true,
      });
    }
  };

  return (
    <div>
      {/* <div className="mb-5">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
          Shipper Revenue
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          View all shippers and their earnings performance
        </p>
      </div> */}

      <TableFilter
        results={allShippers?.data?.length || 0}
        searchPlaceholder={"Search by shipper name, email or phone"}
        title={
          isSearchOpen
            ? `${searchResults.length} search results`
            : `Showing ${allShippers?.data?.length || 0} of ${
                allShippers?.total ?? allShippers?.meta?.total ?? 0
              } results`
        }
        onChange={handleChange}
        handleKeyDown={handleKeyDown}
      >
        <Button.Primary
          title={exporting ? <Beat color={"#ffffff"} /> : "Export"}
          disabled={exporting || !data?.length}
          onClick={handleExport}
        />
      </TableFilter>

      <div className="border-b border-[#E4E4F3]"></div>

      {(filtering && !exporting) || searching ? (
        <TableSkeleton />
      ) : (
        <div className="bg-white dark:bg-[#121212] rounded-md py-4 px-3">
          {data?.length > 0 ? (
            <div>
              <Table
                columns={columns}
                data={data}
                onClick={goToSinglePage}
                goToSinglePage={goToSinglePage}
              />
            </div>
          ) : (
            <div className="bg-white dark:bg-[#121212] rounded-md py-12 px-8 text-center">
              <img
                src={EmptyState}
                alt="empty"
                className="w-32 h-32 mx-auto mb-4"
              />
              <p className="text-gray-500 dark:text-gray-400">
                {isSearchOpen
                  ? "No shippers match your search"
                  : "No shippers found"}
              </p>
            </div>
          )}

          {!isSearchOpen && (
            <PaginationNoRoute
              data={allShippers}
              page={page}
              setPage={setPage}
            />
          )}
        </div>
      )}
    </div>
  );
}
